/**
 * 몬스터 스폰/리스폰 관리. 몬스터 상태의 단일 소유자이며,
 * 추후 멀티플레이에서 서버 권위 모델로 바꿀 때 이 클래스만 동기화 대상이 된다.
 */

import Phaser from 'phaser';
import type { AssetTable } from '../assets/loader';
import { RESPAWN } from '../data/balance';
import type { MapDef } from '../data/maps';
import { MONSTERS } from '../data/monsters';
import { Monster, MONSTER_EVENT } from '../entities/Monster';

interface SpawnPoint {
  monsterId: string;
  x: number;
  y: number;
  patrolMin: number;
  patrolMax: number;
  monster: Monster | null;
  respawnAt: number;
  index: number;
}

export class SpawnDirector {
  readonly group: Phaser.Physics.Arcade.Group;
  private readonly points: SpawnPoint[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly map: MapDef,
    private readonly assets: AssetTable,
    private readonly onKilled: (monster: Monster) => void,
    private readonly onShoot: (monster: Monster, dir: 1 | -1) => void,
  ) {
    this.group = scene.physics.add.group();

    for (const [index, spawn] of map.monsters.entries()) {
      const def = MONSTERS[spawn.monsterId];
      if (!def) continue;
      const bounds = this.patrolBounds(spawn.x, spawn.y, def.patrolRange);
      this.points.push({
        monsterId: spawn.monsterId,
        x: spawn.x,
        y: spawn.y,
        patrolMin: bounds.min,
        patrolMax: bounds.max,
        monster: null,
        respawnAt: 0,
        index,
      });
    }
  }

  get aliveCount(): number {
    return this.points.filter((p) => p.monster && !p.monster.isDying).length;
  }

  get totalCount(): number {
    return this.points.length;
  }

  spawnAll(): void {
    for (const point of this.points) this.spawn(point);
  }

  update(now: number): void {
    for (const point of this.points) {
      if (point.monster && !point.monster.active) point.monster = null;
      if (point.monster) continue;
      if (point.respawnAt === 0) {
        point.respawnAt =
          now + RESPAWN.monsterMs + Phaser.Math.Between(0, RESPAWN.monsterJitterMs);
        continue;
      }
      if (now >= point.respawnAt) this.spawn(point);
    }
  }

  /** 살아있는 몬스터 목록 (전투 판정용) */
  alive(): Monster[] {
    return this.points
      .map((p) => p.monster)
      .filter((m): m is Monster => m !== null && m.active && !m.isDying);
  }

  private spawn(point: SpawnPoint): void {
    const def = MONSTERS[point.monsterId];
    const monster = new Monster(
      this.scene,
      {
        def,
        x: Phaser.Math.Clamp(point.x, point.patrolMin, point.patrolMax),
        y: point.y,
        patrolMin: point.patrolMin,
        patrolMax: point.patrolMax,
      },
      this.assets,
    );

    monster.events.once(MONSTER_EVENT.died, () => {
      this.onKilled(monster);
      point.monster = null;
      point.respawnAt = 0;
    });
    monster.events.on(MONSTER_EVENT.shoot, (p: { monster: Monster; dir: 1 | -1 }) => {
      this.onShoot(p.monster, p.dir);
    });

    this.group.add(monster);
    monster.spawnIndex = point.index;
    monster.spawnKey = `${this.map.id}:${point.index}`;
    point.monster = monster;
    point.respawnAt = 0;

    // 스폰 연출: 살짝 커지며 등장
    monster.setAlpha(0);
    const scale = monster.scaleX;
    monster.setScale(scale * 0.6);
    this.scene.tweens.add({
      targets: monster,
      alpha: 1,
      scaleX: scale,
      scaleY: scale,
      duration: 220,
      ease: 'Back.Out',
    });
  }

  /** 스폰 지점이 놓인 발판 범위 안으로 순찰 구간을 제한한다 (낙하 방지) */
  private patrolBounds(x: number, y: number, range: number): { min: number; max: number } {
    let min = x - range;
    let max = x + range;
    for (const platform of this.map.platforms) {
      if (platform.y !== y) continue;
      if (x < platform.x || x > platform.x + platform.w) continue;
      min = Math.max(min, platform.x + 26);
      max = Math.min(max, platform.x + platform.w - 26);
      break;
    }
    if (min > max) {
      const mid = (min + max) / 2;
      min = mid;
      max = mid;
    }
    return { min, max };
  }
}
